import { subscribe, emit, events } from './eventManager.js';
import { fetchUserProfileInfo, fetchNotifications, fetchNotificationSummary, dismissNotifications, fetchSession } from './apiUtils.js';
const DEFAULT_AUTH_ENV = 'prod';
let account = {
    authenticated: false,
    user: undefined,
    profile: undefined,
    idToken: undefined,
    notifications: undefined,
    notificationSummary: undefined,
};
let jwtAuth = false;
let error = false;
// store the authurl for prod right away incase the initializeAccount function doesn't get ran.
let authenticationUrl = getAuthUrl(DEFAULT_AUTH_ENV);
let extraProfileLinks = [];
const visitor = {
    country: null,
    ip: null,
    isBot: null,
    isInternal: null,
};
export async function initializeAccount(config = {}) {
    const { userRequired = false, customAuthentication = false, redirectUrl = undefined, authEnv = DEFAULT_AUTH_ENV, checkCookiesEnabled = false } = config;
    authenticationUrl = getAuthUrl(authEnv);
    extraProfileLinks = Array.isArray(config.extraProfileLinks) ? config.extraProfileLinks : [];
    // set the account variable just incase it got messed up
    initializeAccountVariable();
    if (customAuthentication) {
        jwtAuth = true;
    }
    // If the consuming app is going to handle their own authentication, don't fetch the user.
    if (!customAuthentication) {
        try {
            const { body, headers } = await fetchSession({ authenticationUrl });
            Object.assign(visitor, mapSessionHeadersToVisitor(headers));
            setVisitor();
            const { authenticated, csrfToken, sid, token: jwt, idToken, user } = body;
            if (userRequired && !authenticated) {
                login(redirectUrl);
            }
            account.authenticated = authenticated;
            account.sid = sid;
            account.csrfToken = csrfToken;
            account.idToken = idToken;
            if (checkCookiesEnabled) {
                // If the user is authenticated, we can assume cookies are enabled.
                if (authenticated) {
                    account.cookiesEnabled = true;
                }
                else {
                    // If the user is not authenticated, check the session a second time to compare session IDs.
                    // If the session ID matches, we can assume cookies are enabled.
                    const { body } = await fetchSession({ authenticationUrl });
                    const { sid: sidCheck } = body;
                    account.cookiesEnabled = sid === sidCheck;
                }
            }
            if (authenticated) {
                account.user = {
                    ...user,
                    jwt,
                };
            }
            setAccount();
        }
        catch (error) {
            setAccount(error);
        }
    }
}
function setAccount(error = false) {
    emit(events.ACCOUNT, account, error);
    // emit event for the depricated subscribeToUser
    emit(events.USER, account.user, error);
}
function setUser(user) {
    account.user = user;
    setAccount();
}
function setVisitor(error = false) {
    emit(events.VISITOR, visitor, error);
}
function subscribeToAccount(callback) {
    return subscribe(events.ACCOUNT, callback);
}
function subscribeToUser(callback) {
    return subscribe(events.USER, callback);
}
function subscribeToVisitor(callback) {
    return subscribe(events.VISITOR, callback);
}
function loadUserNotifications() {
    return fetchNotifications({ authenticationUrl, jwt: account.user?.jwt })
        .then(notifications => {
        account.notifications = notifications;
        error = null;
        emit(events.ACCOUNT, account, error);
    })
        .catch(err => (error = err));
}
function loadUserNotificationSummary() {
    return fetchNotificationSummary({ authenticationUrl, jwt: account.user?.jwt })
        .then(notificationSummary => {
        account.notificationSummary = notificationSummary;
        error = null;
        emit(events.ACCOUNT, account, error);
    })
        .catch(err => (error = err));
}
async function dismissUserNotifications() {
    try {
        await dismissNotifications({ authenticationUrl, jwt: account.user?.jwt });
        // wrap setTimeout in a promise so we can await it
        await new Promise(resolve => setTimeout(resolve, 1000));
        // Query the notification summary because dismissing the notifications causes
        // a change on the api.
        await loadUserNotificationSummary();
    }
    catch (err) {
        error = err;
    }
}
function loadProfile() {
    fetchUserProfileInfo({ authenticationUrl, jwt: account.user?.jwt })
        .then(profile => {
        account.profile = profile;
        error = null;
        emit(events.ACCOUNT, account, error);
    })
        .catch(err => (error = err));
}
function login(redirectUrl = window.location.href) {
    window.location.href = getLoginUrl(redirectUrl);
}
function academicLogin(redirectUrl = window.location.href) {
    window.location.href = getAcademicLoginUrl(redirectUrl);
}
function linkedinLogin(redirectUrl = window.location.href) {
    window.location.href = getLinkedinLoginUrl(redirectUrl);
}
function googleLogin(redirectUrl = window.location.href) {
    window.location.href = getGoogleLoginUrl(redirectUrl);
}
function logout(redirectUrl = window.location.href) {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = getLogoutUrl(redirectUrl);
    const csrfInput = document.createElement('input');
    csrfInput.type = 'hidden';
    csrfInput.name = 'csrfToken';
    csrfInput.value = account.csrfToken ?? '';
    form.appendChild(csrfInput);
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
}
function createAccount(redirectUrl = window.location.href) {
    window.location.href = getCreateAccountUrl(redirectUrl);
}
function getLoginUrl(redirectUrl = window.location.href) {
    return `${authenticationUrl}/login?returnTo=${encodeURIComponent(redirectUrl)}`;
}
function getAcademicLoginUrl(redirectUrl = window.location.href) {
    return `${authenticationUrl}/login/academic?returnTo=${encodeURIComponent(redirectUrl)}`;
}
function getGoogleLoginUrl(redirectUrl = window.location.href) {
    return `${authenticationUrl}/login/google?returnTo=${encodeURIComponent(redirectUrl)}`;
}
function getLinkedinLoginUrl(redirectUrl = window.location.href) {
    return `${authenticationUrl}/login/linkedin?returnTo=${encodeURIComponent(redirectUrl)}`;
}
function getLogoutUrl(redirectUrl = window.location.href) {
    return `${authenticationUrl}/logout?returnTo=${encodeURIComponent(redirectUrl)}`;
}
function getCreateAccountUrl(redirectUrl = window.location.href) {
    return `${authenticationUrl}/login?view=signup&returnTo=${encodeURIComponent(redirectUrl)}`;
}
function getExtraProfileLinks() {
    return extraProfileLinks;
}
function initializeAccountVariable() {
    account = {
        user: undefined,
        profile: undefined,
        notifications: undefined,
        notificationSummary: undefined,
    };
}
export function getAuthUrl(authEnv) {
    // TODO: update with the new production session middleware (this is the webkey one at the moment)
    let authUrl = 'https://auth.sw.siemens.com';
    if (authEnv === 'dev') {
        authUrl = 'https://api.dev.swi.siemens.com/auth';
    }
    else if (authEnv === 'siemens-dev') {
        authUrl = 'https://auth.sw.siemens-dev.com';
    }
    else if (authEnv === 'prod.swi') {
        authUrl = 'https://auth.swi.siemens.com';
    }
    return authUrl;
}
function mapSessionHeadersToVisitor(headers) {
    return {
        country: headers.visitorCountry,
        ip: headers.visitorIP,
        isBot: headers.visitorIsBot,
        isInternal: headers.visitorIsInternal,
    };
}
/**
 * This is a function to return the current account object
 *
 * @return {Object} Account - An object containing account information
 *
 *  @example
 *
 *     getAccount()
 */
export function getAccount() {
    return account;
}
// Put the exports that we want to expose to the module on default
export default {
    subscribeToAccount,
    subscribeToUser,
    subscribeToVisitor,
    setUser,
    loadProfile,
    loadUserNotifications,
    loadUserNotificationSummary,
    dismissUserNotifications,
    login,
    academicLogin,
    linkedinLogin,
    googleLogin,
    logout,
    createAccount,
    getLoginUrl,
    getAcademicLoginUrl,
    getGoogleLoginUrl,
    getLinkedinLoginUrl,
    getLogoutUrl,
    getCreateAccountUrl,
    getAccount,
    getExtraProfileLinks,
};
